import React, { useState, useEffect, useCallback } from 'react';
import { TrendingUp, Syringe, Stethoscope, Scissors, Calendar, CalendarDays, ChevronLeft, ChevronRight, ChevronDown, BarChart3, Package, Banknote, CreditCard, Clock, PawPrint, Briefcase, Box, DoorOpen, Wallet } from 'lucide-react';
import { getDailyStats, getWeeklyStats, getMonthlyStats, getBoardingRevenue, getCustomerBalances } from '../services/api';
import Loader from '../components/Loader';
import BottomNav from '../components/BottomNav';
import UnifiedHistoryModal from '../components/UnifiedHistoryModal';
import PatientReport from '../components/reports/PatientReport';
import OperationalReport from '../components/reports/OperationalReport';
import StockReport from '../components/reports/StockReport';
import TrendReport from '../components/reports/TrendReport';

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

const getMonday = (d) => {
  const date = typeof d === 'string' ? new Date(d + 'T12:00:00') : new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  date.setDate(diff);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const dayNum = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayNum}`;
};

const getLocalDateStr = (d) => {
  const dt = d || new Date();
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const formatCurrency = (amount) => new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', minimumFractionDigits: 0 }).format(amount);
const getDayName = (dateStr) => new Date(dateStr + 'T12:00:00').toLocaleDateString('tr-TR', { weekday: 'short' });

const OperationBreakdown = ({ data, onTypeClick, boardingData }) => (
  <div className="grid grid-cols-5 gap-1.5 mt-4">
    <button onClick={() => onTypeClick?.('vaccine')} className="bg-green-50 rounded-xl p-2.5 text-center hover:bg-green-100 transition-colors">
      <Syringe size={16} className="text-[#34C759] mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{data.vaccine_count || 0}</p>
      <p className="text-[10px] text-[#8E8E93]">{'Aşı'}</p>
    </button>
    <button onClick={() => onTypeClick?.('treatment')} className="bg-blue-50 rounded-xl p-2.5 text-center hover:bg-blue-100 transition-colors">
      <Stethoscope size={16} className="text-[#6b6de3] mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{data.treatment_count || 0}</p>
      <p className="text-[10px] text-[#8E8E93]">Tedavi</p>
    </button>
    <button onClick={() => onTypeClick?.('service')} className="bg-orange-50 rounded-xl p-2.5 text-center hover:bg-orange-100 transition-colors">
      <Scissors size={16} className="text-[#FF9500] mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{data.service_count || 0}</p>
      <p className="text-[10px] text-[#8E8E93]">Hizmet</p>
    </button>
    <button onClick={() => onTypeClick?.('product')} className="bg-purple-50 rounded-xl p-2.5 text-center hover:bg-purple-100 transition-colors">
      <Package size={16} className="text-[#AF52DE] mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{data.product_count || 0}</p>
      <p className="text-[10px] text-[#8E8E93]">{'Ürün'}</p>
    </button>
    <button onClick={() => onTypeClick?.('boarding')} className="bg-amber-50 rounded-xl p-2.5 text-center hover:bg-amber-100 transition-colors" data-testid="boarding-box">
      <DoorOpen size={16} className="text-[#FF9500] mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{boardingData?.count || 0}</p>
      <p className="text-[10px] text-[#8E8E93]">Konaklama</p>
    </button>
  </div>
);

const PaymentBreakdown = ({ data, onPaymentTypeClick }) => (
  <div className="grid grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-4 mt-3">
    <button onClick={() => onPaymentTypeClick?.('cash')} className="bg-emerald-50 rounded-xl p-3 text-center hover:bg-emerald-100 transition-colors" data-testid="report-payment-cash-btn">
      <Banknote size={18} className="text-emerald-600 mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{formatCurrency(data.cash_income || 0)}</p>
      <p className="text-[10px] text-[#8E8E93]">Nakit</p>
    </button>
    <button onClick={() => onPaymentTypeClick?.('card')} className="bg-sky-50 rounded-xl p-3 text-center hover:bg-sky-100 transition-colors" data-testid="report-payment-card-btn">
      <CreditCard size={18} className="text-sky-600 mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{formatCurrency(data.card_income || 0)}</p>
      <p className="text-[10px] text-[#8E8E93]">Kredi Kart{'ı'}</p>
    </button>
    <button onClick={() => onPaymentTypeClick?.('credit')} className="bg-amber-50 rounded-xl p-3 text-center hover:bg-amber-100 transition-colors" data-testid="report-payment-credit-btn">
      <Clock size={18} className="text-amber-600 mx-auto mb-1" />
      <p className="text-base font-bold text-gray-900">{formatCurrency(data.credit_income || 0)}</p>
      <p className="text-[10px] text-[#8E8E93]">Veresiye</p>
    </button>
  </div>
);

const REPORT_CATEGORIES = [
  { key: 'ciro', label: 'Ciro Raporu', icon: Banknote, color: 'text-[#34C759]' },
  { key: 'patient', label: 'Hasta & Müşteri', icon: PawPrint, color: 'text-[#6b6de3]' },
  { key: 'operational', label: 'Operasyonel', icon: Briefcase, color: 'text-[#007AFF]' },
  { key: 'stock', label: 'Stok Raporu', icon: Box, color: 'text-[#AF52DE]' },
  { key: 'trends', label: 'Artış Raporu', icon: TrendingUp, color: 'text-[#34C759]' },
];

const Cash = () => {
  const [activeCategory, setActiveCategory] = useState('ciro');
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('daily');
  const [loading, setLoading] = useState(true);

  // Weekly/Daily state
  const [weekStart, setWeekStart] = useState(getMonday(new Date()));
  const [selectedDate, setSelectedDate] = useState(getLocalDateStr(new Date()));
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [dailyStats, setDailyStats] = useState(null);

  // Monthly state
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [monthlyStats, setMonthlyStats] = useState(null);

  // History modal
  const [historyModal, setHistoryModal] = useState(null);
  const [tabDropdownOpen, setTabDropdownOpen] = useState(false);

  // Boarding & Balance (for Ciro card)
  const [boardingData, setBoardingData] = useState(null);
  const [balances, setBalances] = useState(null);

  // Date range for sub-reports
  const getDateRange = () => {
    if (activeTab === 'daily') return { dateFrom: selectedDate, dateTo: selectedDate };
    if (activeTab === 'weekly') {
      const we = new Date(weekStart + 'T12:00:00');
      we.setDate(we.getDate() + 6);
      return { dateFrom: weekStart, dateTo: getLocalDateStr(we) };
    }
    const [y, m] = selectedMonth.split('-').map(Number);
    return { dateFrom: `${y}-${String(m).padStart(2,'0')}-01`, dateTo: `${y}-${String(m).padStart(2,'0')}-${new Date(y, m, 0).getDate()}` };
  };

  const currentCat = REPORT_CATEGORIES.find(c => c.key === activeCategory) || REPORT_CATEGORIES[0];

  const isCurrentWeek = weekStart === getMonday(new Date());
  const isCurrentMonth = selectedMonth === `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const loadWeeklyData = useCallback(async () => {
    setLoading(true);
    try {
      const we = new Date(weekStart + 'T12:00:00');
      we.setDate(we.getDate() + 6);
      const weTo = getLocalDateStr(we);
      const [weeklyRes, dailyRes, boardingRes] = await Promise.all([
        getWeeklyStats(weekStart),
        getDailyStats(selectedDate),
        getBoardingRevenue(activeTab === 'daily' ? selectedDate : weekStart, activeTab === 'daily' ? selectedDate : weTo)
      ]);
      setWeeklyStats(weeklyRes.data);
      setDailyStats(dailyRes.data);
      setBoardingData(boardingRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [weekStart, selectedDate, activeTab]);

  const loadMonthlyData = useCallback(async () => {
    setLoading(true);
    try {
      const [y, m] = selectedMonth.split('-').map(Number);
      const from = `${y}-${String(m).padStart(2,'0')}-01`;
      const to = `${y}-${String(m).padStart(2,'0')}-${new Date(y, m, 0).getDate()}`;
      const [res, boardingRes] = await Promise.all([
        getMonthlyStats(selectedMonth),
        getBoardingRevenue(from, to)
      ]);
      setMonthlyStats(res.data);
      setBoardingData(boardingRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [selectedMonth]);

  // Load balance once (independent of date)
  useEffect(() => {
    getCustomerBalances().then(r => setBalances(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (activeCategory !== 'ciro') { setLoading(false); return; }
    if (activeTab === 'daily' || activeTab === 'weekly') loadWeeklyData();
    else if (activeTab === 'monthly') loadMonthlyData();
  }, [activeCategory, activeTab, loadWeeklyData, loadMonthlyData]);

  // Week navigation
  const prevWeek = () => {
    const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() - 7);
    const ns = getLocalDateStr(d);
    setWeekStart(ns); setSelectedDate(ns);
  };
  const nextWeek = () => {
    const d = new Date(weekStart + 'T12:00:00'); d.setDate(d.getDate() + 7);
    const ns = getLocalDateStr(d);
    if (ns <= getMonday(new Date())) { setWeekStart(ns); setSelectedDate(ns); }
  };
  const goToCurrentWeek = () => {
    setWeekStart(getMonday(new Date()));
    setSelectedDate(getLocalDateStr());
  };

  // Month navigation
  const prevMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const nextMonth = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    if (y === now.getFullYear() && m >= now.getMonth() + 1) return;
    const d = new Date(y, m, 1);
    setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const formatWeekRange = () => {
    if (!weeklyStats) return '';
    const s = new Date(weeklyStats.week_start + 'T12:00:00');
    const e = new Date(weeklyStats.week_end + 'T12:00:00');
    return `${s.getDate()} ${s.toLocaleDateString('tr-TR', { month: 'short' })} - ${e.getDate()} ${e.toLocaleDateString('tr-TR', { month: 'short', year: 'numeric' })}`;
  };

  const getMonthLabel = () => {
    const [y, m] = selectedMonth.split('-').map(Number);
    return `${MONTHS_TR[m - 1]} ${y}`;
  };

  const openHistoryModal = (mode) => {
    if (activeTab === 'daily') {
      setHistoryModal({ mode, date: selectedDate });
    } else if (activeTab === 'weekly') {
      const we = new Date(new Date(weekStart + 'T12:00:00'));
      we.setDate(we.getDate() + 6);
      setHistoryModal({ mode, dateFrom: weekStart, dateTo: getLocalDateStr(we) });
    } else if (activeTab === 'monthly') {
      const [y, m] = selectedMonth.split('-').map(Number);
      setHistoryModal({ mode, dateFrom: `${y}-${String(m).padStart(2,'0')}-01`, dateTo: `${y}-${String(m).padStart(2,'0')}-${new Date(y, m, 0).getDate()}` });
    }
  };

  const maxDailyIncome = weeklyStats?.daily_stats
    ? Math.max(...weeklyStats.daily_stats.map(d => d.total_income), 1)
    : 1;

  if (loading && activeCategory === 'ciro' && !weeklyStats && !monthlyStats) return <Loader fullScreen />;

  return (
    <div className="min-h-screen bg-[#F2F2F7] pb-24 lg:pb-4">
      <header className="bg-white px-4 pt-12 lg:pt-4 pb-4 shadow-sm">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3">Raporlar</h1>

        {/* Filter Panel */}
        <div className="bg-[#F2F2F7] rounded-2xl p-1.5 space-y-1.5">
          {/* Row 0: Category Dropdown + Period Tab (for ciro only) */}
          <div className="flex items-center gap-1.5">
            {/* Category Dropdown */}
            <div className="relative flex-1">
              <button
                onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                className="w-full flex items-center gap-2 h-9 px-3 bg-white rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50"
                data-testid="report-category-dropdown"
              >
                {React.createElement(currentCat.icon, { size: 14, className: currentCat.color })}
                <span className="flex-1 text-left truncate">{currentCat.label}</span>
                <ChevronDown size={14} className={`text-[#8E8E93] transition-transform ${categoryDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoryDropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setCategoryDropdownOpen(false)} />
                  <div className="absolute left-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[180px]">
                    {REPORT_CATEGORIES.map(cat => {
                      const Icon = cat.icon;
                      const active = activeCategory === cat.key;
                      return (
                        <button
                          key={cat.key}
                          onClick={() => { setActiveCategory(cat.key); setCategoryDropdownOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${active ? 'text-[#6b6de3] bg-[#6b6de3]/5' : 'text-gray-700 hover:bg-[#F2F2F7]'}`}
                          data-testid={`cat-${cat.key}`}
                        >
                          <Icon size={14} className={active ? 'text-[#6b6de3]' : cat.color} />
                          {cat.label}
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* Period Tab Dropdown — only for Ciro */}
            {activeCategory === 'ciro' && (
              <div className="relative">
                <button
                  onClick={() => setTabDropdownOpen(!tabDropdownOpen)}
                  className="flex items-center gap-1.5 h-9 px-3 bg-white rounded-xl text-xs font-semibold text-gray-700 hover:bg-gray-50 whitespace-nowrap"
                  data-testid="report-tab-dropdown"
                >
                  {activeTab === 'daily' ? <Calendar size={13} className="text-[#6b6de3]" /> : activeTab === 'weekly' ? <BarChart3 size={13} className="text-[#6b6de3]" /> : <CalendarDays size={13} className="text-[#6b6de3]" />}
                  {activeTab === 'daily' ? 'Günlük' : activeTab === 'weekly' ? 'Haftalık' : 'Aylık'}
                  <ChevronDown size={14} className={`text-[#8E8E93] transition-transform ${tabDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                {tabDropdownOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setTabDropdownOpen(false)} />
                    <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-xl shadow-lg border border-gray-100 py-1 min-w-[130px]">
                      {[
                        { key: 'daily', label: 'Günlük', icon: Calendar },
                        { key: 'weekly', label: 'Haftalık', icon: BarChart3 },
                        { key: 'monthly', label: 'Aylık', icon: CalendarDays },
                      ].map(tab => {
                        const Icon = tab.icon;
                        const active = activeTab === tab.key;
                        return (
                          <button
                            key={tab.key}
                            onClick={() => { setActiveTab(tab.key); setTabDropdownOpen(false); }}
                            className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-medium transition-colors ${active ? 'text-[#6b6de3] bg-[#6b6de3]/5' : 'text-gray-700 hover:bg-[#F2F2F7]'}`}
                            data-testid={`tab-${tab.key}`}
                          >
                            <Icon size={14} className={active ? 'text-[#6b6de3]' : 'text-[#8E8E93]'} />
                            {tab.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Row 1: Period Navigator (only for Ciro) */}
          {activeCategory === 'ciro' && (activeTab === 'daily' || activeTab === 'weekly') && (
            <div className="flex items-center justify-between bg-white rounded-xl px-2 py-1.5">
              <button onClick={prevWeek} className="p-1 rounded-lg hover:bg-[#F2F2F7] text-[#8E8E93]" data-testid="prev-week-btn">
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={isCurrentWeek ? undefined : goToCurrentWeek}
                className={`text-xs font-semibold px-2 py-0.5 rounded-lg transition-colors ${isCurrentWeek ? 'text-[#6b6de3] bg-[#6b6de3]/10' : 'text-gray-700 hover:bg-[#F2F2F7]'}`}
                data-testid="week-label"
              >
                {isCurrentWeek ? 'Bu Hafta' : formatWeekRange()}
              </button>
              <button onClick={nextWeek} disabled={isCurrentWeek} className="p-1 rounded-lg hover:bg-[#F2F2F7] text-[#8E8E93] disabled:opacity-30" data-testid="next-week-btn">
                <ChevronRight size={16} />
              </button>
            </div>
          )}
          {activeCategory === 'ciro' && activeTab === 'monthly' && (
            <div className="flex items-center justify-between bg-white rounded-xl px-2 py-1.5">
              <button onClick={prevMonth} className="p-1 rounded-lg hover:bg-[#F2F2F7] text-[#8E8E93]" data-testid="prev-month-btn">
                <ChevronLeft size={16} />
              </button>
              <button
                onClick={isCurrentMonth ? undefined : () => setSelectedMonth(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`)}
                className={`text-xs font-semibold px-2 py-0.5 rounded-lg transition-colors ${isCurrentMonth ? 'text-[#6b6de3] bg-[#6b6de3]/10' : 'text-gray-700 hover:bg-[#F2F2F7]'}`}
                data-testid="month-label"
              >
                {isCurrentMonth ? 'Bu Ay' : getMonthLabel()}
              </button>
              <button onClick={nextMonth} disabled={isCurrentMonth} className="p-1 rounded-lg hover:bg-[#F2F2F7] text-[#8E8E93] disabled:opacity-30" data-testid="next-month-btn">
                <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Row 2: Day selector (daily Ciro only) */}
          {activeCategory === 'ciro' && activeTab === 'daily' && weeklyStats?.daily_stats && (
            <div className="flex items-center gap-1 bg-white rounded-xl px-1.5 py-1">
              {weeklyStats.daily_stats.map((day) => {
                const isSelected = day.date === selectedDate;
                const isToday = day.date === getLocalDateStr();
                return (
                  <button
                    key={day.date}
                    onClick={() => setSelectedDate(day.date)}
                    className={`flex-1 flex flex-col items-center py-1 rounded-lg transition-all ${isSelected ? 'bg-[#6b6de3] text-white' : isToday ? 'bg-blue-50' : ''}`}
                    data-testid={`date-${day.date}`}
                  >
                    <span className={`text-[9px] font-medium ${isSelected ? 'text-white/80' : 'text-[#8E8E93]'}`}>{getDayName(day.date)}</span>
                    <span className={`text-xs font-bold ${isSelected ? 'text-white' : isToday ? 'text-[#6b6de3]' : 'text-gray-900'}`}>{parseInt(day.date.split('-')[2], 10)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </header>

      <main className="p-4 space-y-4">
        {activeCategory === 'ciro' && loading && <Loader />}

        {/* ===== SUB-REPORT CATEGORIES ===== */}
        {activeCategory === 'patient' && (
          <div className="animate-fade-in">
            <PatientReport dateFrom={getDateRange().dateFrom} dateTo={getDateRange().dateTo} />
          </div>
        )}
        {activeCategory === 'operational' && (
          <div className="animate-fade-in">
            <OperationalReport dateFrom={getDateRange().dateFrom} dateTo={getDateRange().dateTo} />
          </div>
        )}
        {activeCategory === 'stock' && (
          <div className="animate-fade-in">
            <StockReport />
          </div>
        )}
        {activeCategory === 'trends' && (
          <div className="animate-fade-in">
            <TrendReport />
          </div>
        )}

        {/* ===== CIRO: DAILY TAB ===== */}
        {activeCategory === 'ciro' && activeTab === 'daily' && !loading && dailyStats && (
          <div className="ios-card animate-fade-in" data-testid="daily-summary-card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">
                {new Date(selectedDate + 'T12:00:00').toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long' })}
              </h2>
              <Calendar size={18} className="text-[#8E8E93]" />
            </div>
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-gray-900">{formatCurrency(dailyStats.total_income)}</p>
              <p className="text-sm text-[#8E8E93] mt-1">G{'ü'}nl{'ü'}k Ciro</p>
            </div>
            <OperationBreakdown data={dailyStats} onTypeClick={openHistoryModal} boardingData={boardingData} />
            <PaymentBreakdown data={dailyStats} onPaymentTypeClick={openHistoryModal} />
          </div>
        )}

        {/* ===== CIRO: WEEKLY TAB ===== */}
        {activeCategory === 'ciro' && activeTab === 'weekly' && !loading && (
          <>
            {/* Weekly Chart */}
            {weeklyStats && (
              <div className="ios-card animate-fade-in">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Haftal{'ı'}k {'Ö'}zet</h2>
                  <div className="flex items-center gap-1 text-[#34C759]">
                    <TrendingUp size={16} />
                    <span className="text-sm font-medium">{formatCurrency(weeklyStats.total_income)}</span>
                  </div>
                </div>

                <div className="flex items-end gap-2" style={{ height: '128px' }}>
                  {weeklyStats.daily_stats?.map((day) => {
                    const ratio = maxDailyIncome > 0 ? day.total_income / maxDailyIncome : 0;
                    const barHeight = Math.max(ratio * 100, 4);
                    const isSelected = day.date === selectedDate;
                    return (
                      <button key={day.date} onClick={() => setSelectedDate(day.date)} className="flex-1 flex flex-col items-center justify-end h-full" data-testid={`bar-${day.date}`}>
                        <div className="w-full flex flex-col items-center justify-end flex-1">
                          {day.total_income > 0 && (
                            <span className={`text-[10px] mb-1 ${isSelected ? 'text-[#6b6de3] font-bold' : 'text-[#8E8E93]'}`}>
                              {formatCurrency(day.total_income)}
                            </span>
                          )}
                          <div className={`w-full max-w-[36px] rounded-t-lg transition-all duration-300 ${isSelected ? 'bg-[#6b6de3]' : 'bg-[#E5E5EA]'}`} style={{ height: `${barHeight}px` }} />
                        </div>
                        <span className={`text-xs mt-2 ${isSelected ? 'font-bold text-[#6b6de3]' : 'text-[#8E8E93]'}`}>{getDayName(day.date)}</span>
                      </button>
                    );
                  })}
                </div>

                <OperationBreakdown data={weeklyStats} onTypeClick={openHistoryModal} boardingData={boardingData} />
                <PaymentBreakdown data={weeklyStats} onPaymentTypeClick={openHistoryModal} />

                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
                  <div>
                    <p className="text-sm text-[#8E8E93]">Toplam {'İ'}{'ş'}lem</p>
                    <p className="text-xl font-bold text-gray-900">{weeklyStats.total_operations}</p>
                  </div>
                  <div>
                    <p className="text-sm text-[#8E8E93]">G{'ü'}nl{'ü'}k Ortalama</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(weeklyStats.total_income / 7)}</p>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ===== CIRO: MONTHLY TAB ===== */}
        {activeCategory === 'ciro' && activeTab === 'monthly' && !loading && monthlyStats && (
          <div className="ios-card animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">Ayl{'ı'}k {'Ö'}zet</h2>
              <div className="flex items-center gap-1.5">
                <CalendarDays size={16} className="text-[#8E8E93]" />
                <span className="text-xs text-[#8E8E93]">{getMonthLabel()}</span>
              </div>
            </div>
            <div className="text-center py-4">
              <p className="text-4xl font-bold text-gray-900">{formatCurrency(monthlyStats.total_income)}</p>
              <p className="text-sm text-[#8E8E93] mt-1">Ayl{'ı'}k Toplam Ciro</p>
            </div>
            <OperationBreakdown data={monthlyStats} onTypeClick={openHistoryModal} boardingData={boardingData} />
            <PaymentBreakdown data={monthlyStats} onPaymentTypeClick={openHistoryModal} />
            <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <p className="text-sm text-[#8E8E93]">Toplam {'İ'}{'ş'}lem</p>
                <p className="text-xl font-bold text-gray-900">{monthlyStats.total_operations}</p>
              </div>
              <div>
                <p className="text-sm text-[#8E8E93]">Haftal{'ı'}k Ort.</p>
                <p className="text-xl font-bold text-gray-900">{formatCurrency(monthlyStats.total_income / 4)}</p>
              </div>
            </div>
          </div>
        )}

        {/* ===== BALANCE SUMMARY (always visible in Ciro, not date-filtered) ===== */}
        {activeCategory === 'ciro' && !loading && balances && (
          <div className="ios-card animate-fade-in" data-testid="balance-summary-card">
            <div className="flex items-center gap-2 mb-3">
              <Wallet size={16} className="text-[#6b6de3]" />
              <span className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Müşteri Bakiye Özeti</span>
              <span className="text-[10px] text-[#8E8E93] italic">/ Tarih filtresinden etkilenmez, tüm zamanların bakiye raporudur.</span>
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="text-center">
                <p className="text-xs text-[#8E8E93]">Alacaklı</p>
                <p className="text-sm font-bold text-[#34C759]">{formatCurrency(balances.total_deposit)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#8E8E93]">Borçlu</p>
                <p className="text-sm font-bold text-[#FF3B30]">{formatCurrency(balances.total_withdraw)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-[#8E8E93]">Net Bakiye</p>
                <p className={`text-sm font-bold ${balances.total_balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>{formatCurrency(balances.total_balance)}</p>
              </div>
            </div>
            {balances.customers?.length > 0 && (
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {balances.customers.slice(0, 10).map((c, i) => (
                  <div key={i} className="flex items-center justify-between px-2.5 py-1.5 bg-[#F2F2F7] rounded-xl">
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-gray-900 truncate">{c.name}</p>
                      {c.phone && <p className="text-[10px] text-[#8E8E93]">{c.phone}</p>}
                    </div>
                    <span className={`text-xs font-bold ${c.balance >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>{formatCurrency(c.balance)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <UnifiedHistoryModal
        open={!!historyModal}
        onClose={() => setHistoryModal(null)}
        mode={historyModal?.mode}
        date={historyModal?.date}
        dateFrom={historyModal?.dateFrom}
        dateTo={historyModal?.dateTo}
      />

      <BottomNav />
    </div>
  );
};

export default Cash;
